import React from 'react';
import styled from 'styled-components';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows, FontStacks } from '../constants/theme';
import { InteractiveButton } from './MicroInteractions';

interface Recommendation {
  id: string;
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  timeline: string;
  impact: string;
}

interface RecommendationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  insightTitle: string;
  recommendations: Recommendation[];
}

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 300ms ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.xl}px;
`;

const ModalContainer = styled.div<{ isOpen: boolean }>`
  background: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.elevated};
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  transform: ${props => props.isOpen ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(20px)'};
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid ${Colors.border};
`;

const ModalHeader = styled.div`
  padding: ${Spacing.xxxl}px ${Spacing.xxxl}px ${Spacing.xl}px;
  border-bottom: 1px solid ${Colors.border};
  background: ${Colors.backgroundSecondary};
`;

const ModalTitle = styled.h2`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.xxl}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0 0 ${Spacing.sm}px 0;
  line-height: 1.2;
  letter-spacing: -0.01em;
`;

const ModalSubtitle = styled.p`
  font-size: ${FontSizes.md}px;
  color: ${Colors.textSecondary};
  margin: 0;
  line-height: 1.4;
`;

const ModalContent = styled.div`
  padding: ${Spacing.xxxl}px;
  max-height: 60vh;
  overflow-y: auto;
`;

const RecommendationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xl}px;
`;

const RecommendationCard = styled.div<{ priority: 'high' | 'medium' | 'low' }>`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-left: 4px solid ${props => 
    props.priority === 'high' ? Colors.danger :
    props.priority === 'medium' ? Colors.warning :
    Colors.success
  };
  border-radius: ${BorderRadius.md}px;
  padding: ${Spacing.xl}px;
  transition: all 200ms ease;
  
  &:hover {
    box-shadow: ${Shadows.card};
    border-color: ${props => 
      props.priority === 'high' ? Colors.danger :
      props.priority === 'medium' ? Colors.warning :
      Colors.success
    };
    transform: translateY(-2px);
  }
`;

const RecommendationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${Spacing.md}px;
`;

const RecommendationTitle = styled.h3`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0;
  line-height: 1.3;
  flex: 1;
`;

const PriorityBadge = styled.span<{ priority: 'high' | 'medium' | 'low' }>`
  background: ${props => 
    props.priority === 'high' ? Colors.danger + '20' :
    props.priority === 'medium' ? Colors.warning + '20' :
    Colors.success + '20'
  };
  color: ${props => 
    props.priority === 'high' ? Colors.danger :
    props.priority === 'medium' ? Colors.warning :
    Colors.success
  };
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-left: ${Spacing.md}px;
`;

const RecommendationDescription = styled.p`
  font-size: ${FontSizes.md}px;
  color: ${Colors.textSecondary};
  line-height: 1.6;
  margin: 0 0 ${Spacing.lg}px 0;
`;

const RecommendationMeta = styled.div`
  display: flex;
  gap: ${Spacing.xl}px;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs}px;
`;

const MetaLabel = styled.span`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  font-weight: ${FontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MetaValue = styled.span`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textPrimary};
  font-weight: ${FontWeights.medium};
`;

const ModalFooter = styled.div`
  padding: ${Spacing.xl}px ${Spacing.xxxl}px ${Spacing.xxxl}px;
  border-top: 1px solid ${Colors.border};
  background: ${Colors.backgroundSecondary};
  display: flex;
  justify-content: flex-end;
  gap: ${Spacing.md}px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${Spacing.xl}px;
  right: ${Spacing.xl}px;
  background: none;
  border: none;
  font-size: ${FontSizes.xl}px;
  color: ${Colors.textTertiary};
  cursor: pointer;
  padding: ${Spacing.sm}px;
  border-radius: ${BorderRadius.sm}px;
  transition: all 200ms ease;
  
  &:hover {
    background: ${Colors.surfaceHover};
    color: ${Colors.textPrimary};
  }
  
  &:focus {
    outline: none;
    background: ${Colors.surfaceSelected};
    color: ${Colors.primary};
  }
`;

const RecommendationsModal: React.FC<RecommendationsModalProps> = ({
  isOpen,
  onClose,
  insightTitle,
  recommendations
}) => {
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContainer isOpen={isOpen}>
        <CloseButton onClick={onClose} aria-label="Close recommendations">
          ×
        </CloseButton>
        
        <ModalHeader>
          <ModalTitle>AI Recommendations</ModalTitle>
          <ModalSubtitle>{insightTitle}</ModalSubtitle>
        </ModalHeader>

        <ModalContent>
          <RecommendationsList>
            {recommendations.map((recommendation) => (
              <RecommendationCard key={recommendation.id} priority={recommendation.priority}>
                <RecommendationHeader>
                  <RecommendationTitle>{recommendation.title}</RecommendationTitle>
                  <PriorityBadge priority={recommendation.priority}>
                    {recommendation.priority} priority
                  </PriorityBadge>
                </RecommendationHeader>
                
                <RecommendationDescription>
                  {recommendation.description}
                </RecommendationDescription>
                
                <RecommendationMeta>
                  <MetaItem>
                    <MetaLabel>Timeline</MetaLabel>
                    <MetaValue>{recommendation.timeline}</MetaValue>
                  </MetaItem>
                  <MetaItem>
                    <MetaLabel>Expected Impact</MetaLabel>
                    <MetaValue>{recommendation.impact}</MetaValue>
                  </MetaItem>
                </RecommendationMeta>
              </RecommendationCard>
            ))}
          </RecommendationsList>
        </ModalContent>

        <ModalFooter>
          <InteractiveButton variant="secondary" size="md" onClick={onClose}>
            Close
          </InteractiveButton>
          <InteractiveButton variant="primary" size="md">
            Implement Selected
          </InteractiveButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default RecommendationsModal;